import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { filterRolesByHierarchy } from '../../utils/roleUtils';
import UserForm from '../../components/forms/UserForm';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IUser {
  staffProfile?: { aboutMe: string; photoUrl: string | null };
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  admin: boolean;
  role?: { _id: string; name: string; permissions?: Record<string, number>; isSystem?: boolean };
}

interface IMember {
  _id: string;
  name: string;
  user?: { _id: string };
}

interface IRole {
  _id: string;
  name: string;
  permissions?: Record<string, number>;
  isSystem?: boolean;
}

const UserEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { currentMembership } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<IUser | null>(null);
  const [assignedMembers, setAssignedMembers] = useState<IMember[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [userResponse, membersResponse, rolesResponse] = await Promise.all([
          api.users.get(id!),
          api.members.list(),
          api.roles.list(),
        ]);

        const userData = userResponse.data as IUser;
        setUser(userData);

        // Filter members assigned to this user
        const members = membersResponse.data as IMember[];
        const userMembers = members.filter(m => m.user && m.user._id === id);
        setAssignedMembers(userMembers);

        // Filter roles by hierarchy
        const allRoles = rolesResponse.data as IRole[];
        const filtered = filterRolesByHierarchy(allRoles, currentMembership?.role as IRole | undefined);
        setRoles(filtered as IRole[]);

        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t));
        setLoading(false);
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else if (err.response && err.response.status === 404) {
          navigate('/admin/users');
        }
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate, currentMembership]);

  const submitHandler = async (data: { name: string; email: string; phoneNumber: string; password?: string; roleId?: string; staffProfile?: { aboutMe: string; photoUrl: string | null } }) => {
    setSaving(true);
    setError(null);

    try {
      const updateData: { name: string; email: string; phoneNumber: string; password?: string; roleId?: string; staffProfile?: { aboutMe: string; photoUrl: string | null } } = {
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        staffProfile: data.staffProfile,
      };

      if (data.password) {
        updateData.password = data.password;
      }
      if (data.roleId) {
        updateData.roleId = data.roleId;
      }

      await api.users.update(id!, updateData);
      setSaving(false);
      addToast(t('users.editTitle') + ' - ' + t('common.save') + '!', 'success');
      navigate('/admin/users');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setSaving(false);
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
    }
  };

  if (loading) return <p>{t('users.loadingUser')}</p>;
  if (error && !user) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!user) return <p>{t('users.userNotFound')}</p>;

  return (
    <div className="admin-form-page">
      <h2>{t('users.editTitle')}</h2>
      {error && <p className="error-message">{t('common.error')}: {error}</p>}
      <UserForm
        initialData={{
          name: user.name,
          staffProfile: user.staffProfile,
          email: user.email,
          phoneNumber: user.phoneNumber || '',
          roleId: user.role?._id,
        }}
        seedKey={user._id}
        isEditMode
        assignedMembers={assignedMembers}
        roles={roles}
        onSubmit={submitHandler}
        loading={saving}
        submitButtonText={t('users.updateButton')}
      />
      <Link to="/admin/users" className="form-link">{t('users.goBack')}</Link>
    </div>
  );
};

export default UserEditPage;
